import React, { useState, useEffect } from 'react';
import {
  Typography,
  Grid,
  Paper,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  ComposedChart
} from 'recharts';
import {
  getTsiOpenVsClosedChart,
  getTsiClosedByClassificationChart,
  getTsiDailyCreatedVsClosedChart,
  getTsiAssignees,
  getTsiAverageAgeByCreatedMonth,
  getTsiAverageAgeByClosedMonth,
  getTsiWeeklyIncomingByEnvironmentChart,
  getTsiWeeklyIncomingByClassificationChart,
  getTsiTickets,
  getTsiTicketStats,
  getTsiAssigneePivotTable,
  getAdoEmailGroups
} from '../../services/apiService';
import TsiClassificationPivotTable from './TsiClassificationPivotTable';
import TsiAssigneePivotTable from './TsiAssigneePivotTable';

/**
 * TSI Charts component for displaying TSI ticket analytics
 * @returns {JSX.Element} TSI Charts component
 */
const TsiCharts = () => {
  // State for chart data
  const [openVsClosedData, setOpenVsClosedData] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [closedByClassificationData, setClosedByClassificationData] = useState([]);
  const [averageAgeByCreatedMonth, setAverageAgeByCreatedMonth] = useState([]);
  const [averageAgeByClosedMonth, setAverageAgeByClosedMonth] = useState([]);
  const [weeklyIncomingByEnvironmentData, setWeeklyIncomingByEnvironmentData] = useState([]);
  const [weeklyIncomingByClassificationData, setWeeklyIncomingByClassificationData] = useState([]);
  const [assigneePivotData, setAssigneePivotData] = useState(null);

  // Daily Created vs Closed (last 60 days) + its assignee multi-select filter.
  const [dailyCreatedVsClosedData, setDailyCreatedVsClosedData] = useState([]);
  const [loadingDaily, setLoadingDaily] = useState(true);
  const [errorDaily, setErrorDaily] = useState(null);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [selectedAssignees, setSelectedAssignees] = useState([]); // [] = all assignees

  // Group filter state
  const [selectedGroup, setSelectedGroup] = useState('');
  const [adoEmailGroups, setAdoEmailGroups] = useState({
    sreGroup: '',
    professionalServiceGroup: '',
    clientSupportTeam: ''
  });
  const [loadingEmailGroups, setLoadingEmailGroups] = useState(true);
  
  // Loading states
  const [loadingOpenVsClosed, setLoadingOpenVsClosed] = useState(true);
  const [loadingClosedByClassification, setLoadingClosedByClassification] = useState(true);
  const [loadingAverageAgeByCreated, setLoadingAverageAgeByCreated] = useState(true);
  const [loadingAverageAgeByClosed, setLoadingAverageAgeByClosed] = useState(true);
  const [loadingWeeklyIncomingByEnvironment, setLoadingWeeklyIncomingByEnvironment] = useState(true);
  const [loadingWeeklyIncomingByClassification, setLoadingWeeklyIncomingByClassification] = useState(true);
  
  // Error states
  const [errorOpenVsClosed, setErrorOpenVsClosed] = useState(null);
  const [errorClosedByClassification, setErrorClosedByClassification] = useState(null);
  const [errorAverageAgeByCreated, setErrorAverageAgeByCreated] = useState(null);
  const [errorAverageAgeByClosed, setErrorAverageAgeByClosed] = useState(null);
  const [errorWeeklyIncomingByEnvironment, setErrorWeeklyIncomingByEnvironment] = useState(null);
  const [errorWeeklyIncomingByClassification, setErrorWeeklyIncomingByClassification] = useState(null);


  // Fetch open vs closed data
  const fetchOpenVsClosedData = async () => {
    try {
      setLoadingOpenVsClosed(true);
      setErrorOpenVsClosed(null);
      
      const response = await getTsiOpenVsClosedChart();
      
      if (response.success) {
        // Set environments for color mapping
        setEnvironments(response.environments || []);
        
        // Transform data for stacked chart. Backlog is the true point-in-time open count
        // computed by the backend (cumulative created − closed over all history), so we
        // use it directly rather than recomputing a window-only running sum here.
        const chartData = response.data.map((item) => {
          // Build the chart data with environment-specific data
          const monthData = {
            month: `${item.monthName} ${item.year}`,
            totalOpened: item.totalOpened,
            totalClosed: item.totalClosed,
            backlog: item.backlog ?? 0
          };
          
          // Add environment-specific data for stacking
          (response.environments || []).forEach(env => {
            monthData[`created_${env}`] = item[`created_${env}`] || 0;
            monthData[`closed_${env}`] = item[`closed_${env}`] || 0;
          });
          
          return monthData;
        });
        
        setOpenVsClosedData(chartData);
      } else {
        throw new Error(response.message || 'Failed to fetch open vs closed data');
      }
    } catch (error) {
      console.error('Error fetching open vs closed data:', error);
      setErrorOpenVsClosed(error.message);
    } finally {
      setLoadingOpenVsClosed(false);
    }
  };

  // Fetch closed by classification data
  const fetchClosedByClassificationData = async () => {
    try {
      setLoadingClosedByClassification(true);
      setErrorClosedByClassification(null);
      
      const response = await getTsiClosedByClassificationChart();
      
      if (response.success) {
        // Transform data for stacked bar chart
        const chartData = response.data.map(item => {
          const monthData = {
            month: `${item.monthName} ${item.year}`,
            total: item.total
          };
          
          // Add each classification as a separate field
          item.classifications.forEach(classification => {
            if (classification.classification) {
              monthData[classification.classification] = classification.count;
            }
          });
          
          return monthData;
        });
        
        setClosedByClassificationData(chartData);
      } else {
        throw new Error(response.message || 'Failed to fetch closed by classification data');
      }
    } catch (error) {
      console.error('Error fetching closed by classification data:', error);
      setErrorClosedByClassification(error.message);
    } finally {
      setLoadingClosedByClassification(false);
    }
  };

  // Fetch average age by created month data
  const fetchAverageAgeByCreatedMonth = async () => {
    try {
      setLoadingAverageAgeByCreated(true);
      setErrorAverageAgeByCreated(null);
      
      const response = await getTsiAverageAgeByCreatedMonth();
      
      if (response.success) {
        const chartData = response.data.map(item => ({
          month: `${item.monthName} ${item.year}`,
          averageAge: item.averageAge,
          ticketCount: item.ticketCount
        }));
        
        setAverageAgeByCreatedMonth(chartData);
      } else {
        throw new Error(response.message || 'Failed to fetch average age by created month data');
      }
    } catch (error) {
      console.error('Error fetching average age by created month data:', error);
      setErrorAverageAgeByCreated(error.message);
    } finally {
      setLoadingAverageAgeByCreated(false);
    }
  };

  // Fetch average age by closed month data
  const fetchAverageAgeByClosedMonth = async () => {
    try {
      setLoadingAverageAgeByClosed(true);
      setErrorAverageAgeByClosed(null);
      
      const response = await getTsiAverageAgeByClosedMonth();
      
      if (response.success) {
        const chartData = response.data.map(item => ({
          month: `${item.monthName} ${item.year}`,
          averageAge: item.averageAge,
          ticketCount: item.ticketCount
        }));
        
        setAverageAgeByClosedMonth(chartData);
      } else {
        throw new Error(response.message || 'Failed to fetch average age by closed month data');
      }
    } catch (error) {
      console.error('Error fetching average age by closed month data:', error);
      setErrorAverageAgeByClosed(error.message);
    } finally {
      setLoadingAverageAgeByClosed(false);
    }
  };

  // Fetch weekly incoming by environment data
  const fetchWeeklyIncomingByEnvironmentData = async () => {
    try {
      setLoadingWeeklyIncomingByEnvironment(true);
      setErrorWeeklyIncomingByEnvironment(null);
      
      const response = await getTsiWeeklyIncomingByEnvironmentChart();
      
      if (response.success) {
        // Transform data for stacked bar chart
        const chartData = response.data.map(item => {
          const weekData = {
            week: `Week of ${new Date(item.weekStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
            total: item.total
          };
          
          // Add each environment as a separate field
          item.environments.forEach(env => {
            if (env.environment) {
              weekData[env.environment] = env.count;
            }
          });
          
          return weekData;
        });
        
        setWeeklyIncomingByEnvironmentData(chartData);
      } else {
        throw new Error(response.message || 'Failed to fetch weekly incoming by environment data');
      }
    } catch (error) {
      console.error('Error fetching weekly incoming by environment data:', error);
      setErrorWeeklyIncomingByEnvironment(error.message);
    } finally {
      setLoadingWeeklyIncomingByEnvironment(false);
    }
  };

  // Fetch weekly incoming by classification data
  const fetchWeeklyIncomingByClassificationData = async () => {
    try {
      setLoadingWeeklyIncomingByClassification(true);
      setErrorWeeklyIncomingByClassification(null);
      
      const response = await getTsiWeeklyIncomingByClassificationChart();
      
      if (response.success) {
        // Transform data for stacked bar chart
        const chartData = response.data.map(item => {
          const weekData = {
            week: `Week of ${new Date(item.weekStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
            total: item.total
          };
          
          // Add each classification as a separate field
          item.classifications.forEach(cls => {
            if (cls.classification) {
              weekData[cls.classification] = cls.count;
            }
          });
          
          return weekData;
        });
        
        setWeeklyIncomingByClassificationData(chartData);
      } else {
        throw new Error(response.message || 'Failed to fetch weekly incoming by classification data');
      }
    } catch (error) {
      console.error('Error fetching weekly incoming by classification data:', error);
      setErrorWeeklyIncomingByClassification(error.message);
    } finally {
      setLoadingWeeklyIncomingByClassification(false);
    }
  };

  // Fetch assignee pivot table data
  const fetchAssigneePivotData = async () => {
    try {
      const response = await getTsiAssigneePivotTable();
      
      if (response.success) {
        console.log("Assignee pivot data:", response);
        setAssigneePivotData(response);
      } else {
        console.error("Failed to fetch assignee pivot data:", response.message);
      }
    } catch (error) {
      console.error('Error fetching assignee pivot data:', error);
    }
  };

  // Fetch daily Created vs Closed (last 60 days), honoring the assignee filter.
  const fetchDailyCreatedVsClosed = async (assignees = selectedAssignees) => {
    try {
      setLoadingDaily(true);
      setErrorDaily(null);
      // Empty OR "everyone selected" both mean no filter — send an empty list so the
      // backend returns all tickets without a large query string.
      const allSelected = assigneeOptions.length > 0 && assignees.length === assigneeOptions.length;
      const effective = allSelected ? [] : assignees;
      const response = await getTsiDailyCreatedVsClosedChart({ days: 60, assignees: effective });
      if (response.success) {
        setDailyCreatedVsClosedData(response.data || []);
      } else {
        throw new Error(response.message || 'Failed to fetch daily created vs closed data');
      }
    } catch (error) {
      console.error('Error fetching daily created vs closed data:', error);
      setErrorDaily(error.message);
    } finally {
      setLoadingDaily(false);
    }
  };

  // Fetch the assignee options for the daily-chart multi-select. Defaults the selection
  // to everyone (all boxes ticked). The full-selection sentinel is treated as "all" by
  // the chart fetch (see fetchDailyCreatedVsClosed), so no giant query string is sent.
  const fetchAssigneeOptions = async () => {
    try {
      const response = await getTsiAssignees();
      if (response.success) {
        const options = response.data || [];
        setAssigneeOptions(options);
        setSelectedAssignees(options); // default: everyone selected
      }
    } catch (error) {
      console.error('Error fetching assignee options:', error);
    }
  };

  // Fetch ADO email groups
  const fetchAdoEmailGroups = async () => {
    try {
      setLoadingEmailGroups(true);
      const response = await getAdoEmailGroups();
      if (response.success && response.data) {
        setAdoEmailGroups({
          sreGroup: response.data.sreGroup || '',
          professionalServiceGroup: response.data.professionalServiceGroup || '',
          clientSupportTeam: response.data.clientSupportTeam || ''
        });
      }
    } catch (err) {
      console.error('Error fetching ADO email groups:', err);
    } finally {
      setLoadingEmailGroups(false);
    }
  };
  
  // Load all chart data on component mount
  useEffect(() => {
    fetchAdoEmailGroups();
    fetchOpenVsClosedData();
    fetchClosedByClassificationData();
    fetchAverageAgeByCreatedMonth();
    fetchAverageAgeByClosedMonth();
    fetchWeeklyIncomingByEnvironmentData();
    fetchWeeklyIncomingByClassificationData();
    fetchAssigneePivotData();
    fetchAssigneeOptions();
    fetchDailyCreatedVsClosed([]);
  }, []);

  // Re-fetch just the daily chart whenever the assignee selection changes (skip the
  // initial [] render — the mount effect already loaded the unfiltered chart).
  const didMountDaily = React.useRef(false);
  useEffect(() => {
    if (!didMountDaily.current) { didMountDaily.current = true; return; }
    fetchDailyCreatedVsClosed(selectedAssignees);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssignees]);
  
  // Reload chart data when group filter changes
  useEffect(() => {
    if (!loadingEmailGroups) {
      // Note: Backend support needed for group filtering in charts
      // For now, the filter UI is ready but charts will show all data
      // Once backend supports group filtering, uncomment below:
      // fetchOpenVsClosedData();
      // fetchClosedByClassificationData();
      // fetchAverageAgeByCreatedMonth();
      // fetchAverageAgeByClosedMonth();
      // fetchWeeklyIncomingByEnvironmentData();
      // fetchWeeklyIncomingByClassificationData();
      // fetchAssigneePivotData();
    }
  }, [selectedGroup, loadingEmailGroups]);

  // Get unique classifications for legend
  const getClassifications = () => {
    if (closedByClassificationData.length === 0) return [];
    
    const classifications = new Set();
    closedByClassificationData.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key !== 'month' && key !== 'total') {
          classifications.add(key);
        }
      });
    });
    
    return Array.from(classifications);
  };

  // Get unique environments for weekly incoming chart
  const getWeeklyEnvironments = () => {
    if (weeklyIncomingByEnvironmentData.length === 0) return [];
    
    const environments = new Set();
    weeklyIncomingByEnvironmentData.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key !== 'week' && key !== 'total') {
          environments.add(key);
        }
      });
    });
    
    return Array.from(environments);
  };

  // Get unique classifications for weekly incoming chart
  const getWeeklyClassifications = () => {
    if (weeklyIncomingByClassificationData.length === 0) return [];
    
    const classifications = new Set();
    weeklyIncomingByClassificationData.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key !== 'week' && key !== 'total') {
          classifications.add(key);
        }
      });
    });
    
    return Array.from(classifications);
  };

  // Color schemes for charts
  const openClosedColors = {
    opened: '#FF6B6B',
    closed: '#4ECDC4'
  };

  const classificationColors = [
    '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#8dd1e1', 
    '#d084d0', '#ffb347', '#87ceeb', '#dda0dd', '#98fb98'
  ];

  // Priority colors mapping: 1=Red, 2=Amber, 3=Green, 4=Blue
  const priorityColors = {
    '1': '#FF0000',  // Red
    '2': '#FFC000',  // Amber
    '3': '#00B050',  // Green
    '4': '#0070C0'   // Blue
  };

  // Generate environment colors - red shades for created, darker green shades for closed
  const generateEnvironmentColors = () => {
    const colors = {};
    
    // Red shades for created tickets (from light to dark)
    const redShades = ['#FFB3B3', '#FF8080', '#FF4D4D', '#FF1A1A', '#E60000', '#B30000'];
    
    // Darker green shades for closed tickets (more visible)
    const greenShades = ['#228B22', '#32CD32', '#00AA00', '#008000', '#006400', '#004000'];
    
    environments.forEach((env, index) => {
      const colorIndex = index % 6; // Cycle through 6 shades
      colors[`created_${env}`] = redShades[colorIndex];
      colors[`closed_${env}`] = greenShades[colorIndex];
    });
    
    return colors;
  };

  const environmentColors = generateEnvironmentColors();

  // Environment colors for weekly incoming chart
  const weeklyEnvironmentColors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8',
    '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8C471', '#82E0AA'
  ];

  // Custom label component for bar segments
  const CustomLabel = (props) => {
    const { x, y, width, height, value } = props;
    
    // Only show label if value exists and is greater than 0
    if (!value || value === 0) return null;
    
    return (
      <text 
        x={x + width / 2} 
        y={y - 5} 
        fill="#000000" 
        textAnchor="middle" 
        dominantBaseline="bottom"
        fontSize="12"
        fontWeight="bold"
        style={{ pointerEvents: 'none' }}
      >
        {value}
      </text>
    );
  };

  // Custom tooltip to show individual values and total
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
      
      return (
        <div style={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.95)', 
          padding: '10px', 
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: '2px 0', color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
          <p style={{ 
            margin: '5px 0 0 0', 
            paddingTop: '5px', 
            borderTop: '1px solid #ddd',
            fontWeight: 'bold' 
          }}>
            Total: {total}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
      <Box mt={4}>
      <Typography variant="h5" gutterBottom sx={{ color: "#1D4434", fontWeight: 'bold' }}>
        TSI Analytics & Charts
      </Typography>
      
      {/* Filter Bar */}
      <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Grid container spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <Grid item sx={{ flexShrink: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mr: 2, whiteSpace: 'nowrap' }}>
              Filters
            </Typography>
          </Grid>
          <Grid item xs={12} sm="auto" sx={{ minWidth: '120px', flex: '1 1 0' }}>
            <FormControl fullWidth size="small">
              <InputLabel>State</InputLabel>
              <Select label="State">
                <MenuItem value="">All</MenuItem>
                <MenuItem value="New">New</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Resolved">Resolved</MenuItem>
                <MenuItem value="Closed">Closed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm="auto" sx={{ minWidth: '120px', flex: '1 1 0' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Team</InputLabel>
              <Select label="Team">
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Engineering">Engineering</MenuItem>
                <MenuItem value="QA">QA</MenuItem>
                <MenuItem value="Support">Support</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm="auto" sx={{ minWidth: '140px', flex: '1 1 0' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Environment</InputLabel>
              <Select label="Environment">
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Production">Production</MenuItem>
                <MenuItem value="Staging">Staging</MenuItem>
                <MenuItem value="Development">Development</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm="auto" sx={{ minWidth: '130px', flex: '1 1 0' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Component</InputLabel>
              <Select label="Component">
                <MenuItem value="">All</MenuItem>
                <MenuItem value="UI">UI</MenuItem>
                <MenuItem value="API">API</MenuItem>
                <MenuItem value="Database">Database</MenuItem>
                <MenuItem value="Authentication">Authentication</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm="auto" sx={{ minWidth: '100px', flex: '0 0 auto' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select label="Priority">
                <MenuItem value="">All</MenuItem>
                <MenuItem value="1">1</MenuItem>
                <MenuItem value="2">2</MenuItem>
                <MenuItem value="3">3</MenuItem>
                <MenuItem value="4">4</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm="auto" sx={{ minWidth: '160px', flex: '1 1 0' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Group</InputLabel>
              <Select 
                label="Group"
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                disabled={loadingEmailGroups}
              >
                <MenuItem value="">All Groups</MenuItem>
                <MenuItem value="SRE Group">SRE Group</MenuItem>
                <MenuItem value="Professional Service Group">Professional Service Group</MenuItem>
                <MenuItem value="Client Support Team">Client Support Team</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm="auto" sx={{ flexShrink: 0 }}>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<SearchIcon />}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Apply
            </Button>
          </Grid>
        </Grid>
      </Box>
      
      <Grid container spacing={3}>
        {/* Chart 1: Open vs Closed by Month */}
        <Grid item xs={12}>
          <Card
            sx={{
              borderRadius: 2,
              border: '1px solid rgba(0, 0, 0, 0.06)',
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-2px)',
              },
            }}
          >
            <CardHeader 
              title="Open vs Closed Tickets by Month (July 2024 onwards)"
              sx={{ 
                bgcolor: '#1D4434', 
                color: 'white',
                '& .MuiCardHeader-title': {
                  fontWeight: 600,
                },
              }}
            />
            <CardContent>
              {loadingOpenVsClosed ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                  <CircularProgress />
                </Box>
              ) : errorOpenVsClosed ? (
                <Alert severity="error">{errorOpenVsClosed}</Alert>
              ) : openVsClosedData.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                  <Typography color="textSecondary">No data available</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <ComposedChart data={openVsClosedData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip />
                    <Legend />
                    
                    {/* Stacked bars for created tickets by environment (red shades) */}
                    {environments.map((env) => (
                      <Bar 
                        key={`created_${env}`}
                        yAxisId="left" 
                        dataKey={`created_${env}`} 
                        stackId="created"
                        fill={environmentColors[`created_${env}`]}
                        name={`Created - ${env}`}
                        label={<CustomLabel />}
                      />
                    ))}
                    
                    {/* Stacked bars for closed tickets by environment (green shades) */}
                    {environments.map((env) => (
                      <Bar 
                        key={`closed_${env}`}
                        yAxisId="left" 
                        dataKey={`closed_${env}`} 
                        stackId="closed"
                        fill={environmentColors[`closed_${env}`]}
                        name={`Closed - ${env}`}
                        label={<CustomLabel />}
                      />
                    ))}
                    
                    <Line 
                      yAxisId="right" 
                      type="monotone" 
                      dataKey="backlog" 
                      stroke="#4BACC6" 
                      strokeWidth={3}
                      dot={{ fill: '#4BACC6', strokeWidth: 2, r: 4 }}
                      name="Backlog"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Chart 1b: Daily Created vs Closed (last 60 days) with an assignee multi-select filter */}
        <Grid item xs={12}>
          <Card
            sx={{
              borderRadius: 2,
              border: '1px solid rgba(0, 0, 0, 0.06)',
              overflow: 'hidden',
            }}
          >
            <CardHeader
              title="Daily Created vs Closed Tickets (Last 60 Days of Activity)"
              subheader={
                dailyCreatedVsClosedData.length > 0
                  ? `${new Date(dailyCreatedVsClosedData[0].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} – ${new Date(dailyCreatedVsClosedData[dailyCreatedVsClosedData.length - 1].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                  : undefined
              }
              sx={{
                bgcolor: '#1D4434',
                color: 'white',
                '& .MuiCardHeader-title': { fontWeight: 600 },
                '& .MuiCardHeader-subheader': { color: 'rgba(255,255,255,0.75)' },
              }}
            />
            <CardContent>
              {/* Assignee multi-select — scopes ONLY this chart. Defaults to everyone
                  selected; selecting all (or none) means all assignees. */}
              <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 320, maxWidth: 520 }}>
                  <InputLabel id="daily-assignee-label">Assignees</InputLabel>
                  <Select
                    labelId="daily-assignee-label"
                    multiple
                    value={selectedAssignees}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSelectedAssignees(typeof v === 'string' ? v.split(',') : v);
                    }}
                    input={<OutlinedInput label="Assignees" />}
                    renderValue={(selected) => {
                      if (assigneeOptions.length > 0 && selected.length === assigneeOptions.length) {
                        return `All assignees (${assigneeOptions.length})`;
                      }
                      if (selected.length === 0) return 'All assignees';
                      if (selected.length > 3) return `${selected.length} assignees selected`;
                      return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      );
                    }}
                    MenuProps={{ PaperProps: { style: { maxHeight: 360 } } }}
                  >
                    {assigneeOptions.map((name) => (
                      <MenuItem key={name} value={name}>
                        <Checkbox checked={selectedAssignees.indexOf(name) > -1} />
                        <ListItemText primary={name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  size="small"
                  onClick={() => setSelectedAssignees(assigneeOptions)}
                  disabled={selectedAssignees.length === assigneeOptions.length}
                >
                  Select all
                </Button>
                {selectedAssignees.length > 0 && selectedAssignees.length < assigneeOptions.length && (
                  <Button size="small" onClick={() => setSelectedAssignees([])} startIcon={<ClearIcon />}>
                    Clear
                  </Button>
                )}
              </Box>

              {loadingDaily ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                  <CircularProgress />
                </Box>
              ) : errorDaily ? (
                <Alert severity="error">{errorDaily}</Alert>
              ) : dailyCreatedVsClosedData.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                  <Typography color="textSecondary">No data available</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={dailyCreatedVsClosedData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      interval="preserveStartEnd"
                      minTickGap={20}
                    />
                    <YAxis allowDecimals={false} />
                    <Tooltip
                      labelFormatter={(d) => new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                    />
                    <Legend />
                    <Bar dataKey="created" name="Created" fill="#C0504D" maxBarSize={18} />
                    <Bar dataKey="closed" name="Closed" fill="#4F81BD" maxBarSize={18} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Chart 2: Closed Tickets by Classification (full width on smaller screens, 50% on larger) */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Closed Tickets by Month - Grouped by Investigation Classification"
              sx={{ bgcolor: '#2E7D32', color: 'white' }}
            />
            <CardContent>
              {loadingClosedByClassification ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                  <CircularProgress />
                </Box>
              ) : errorClosedByClassification ? (
                <Alert severity="error">{errorClosedByClassification}</Alert>
              ) : closedByClassificationData.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                  <Typography color="textSecondary">No data available</Typography>
                </Box>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={closedByClassificationData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      {getClassifications().map((classification, index) => (
                        <Bar 
                          key={classification}
                          dataKey={classification} 
                          stackId="a"
                          fill={classificationColors[index % classificationColors.length]}
                          name={classification}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                  
                  {/* Add the pivot tables below the chart */}
                  <Box mt={4}>
                    <TsiClassificationPivotTable data={closedByClassificationData} />
                    <Box mt={4}>
                      {assigneePivotData ? (
                        <TsiAssigneePivotTable data={assigneePivotData} />
                      ) : (
                        <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                          <CircularProgress size={30} />
                          <Typography variant="body1" color="textSecondary" sx={{ ml: 2 }}>
                            Loading assignee data...
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Chart 3b: Weekly Incoming by Environment */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="Weekly Incoming Tickets by Environment (Last 10 Weeks)"
              sx={{ bgcolor: '#FF5722', color: 'white' }}
            />
            <CardContent>
              {loadingWeeklyIncomingByEnvironment ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                  <CircularProgress />
                </Box>
              ) : errorWeeklyIncomingByEnvironment ? (
                <Alert severity="error">{errorWeeklyIncomingByEnvironment}</Alert>
              ) : weeklyIncomingByEnvironmentData.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                  <Typography color="textSecondary">No data available</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={weeklyIncomingByEnvironmentData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }} barCategoryGap="20%">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="week" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    {getWeeklyEnvironments().map((environment, index) => (
                      <Bar 
                        key={environment}
                        dataKey={environment} 
                        fill={weeklyEnvironmentColors[index % weeklyEnvironmentColors.length]}
                        name={environment}
                        label={<CustomLabel />}
                        maxBarSize={60}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Chart 3c: Weekly Incoming by Classification */}
        <Grid item xs={12}>
          <Card>
            <CardHeader 
              title="Weekly Incoming Tickets by Investigation Classification (Last 10 Weeks)"
              sx={{ bgcolor: '#673AB7', color: 'white' }}
            />
            <CardContent>
              {loadingWeeklyIncomingByClassification ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                  <CircularProgress />
                </Box>
              ) : errorWeeklyIncomingByClassification ? (
                <Alert severity="error">{errorWeeklyIncomingByClassification}</Alert>
              ) : weeklyIncomingByClassificationData.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                  <Typography color="textSecondary">No data available</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={weeklyIncomingByClassificationData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }} barCategoryGap="15%">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="week" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    {getWeeklyClassifications().map((classification, index) => (
                      <Bar 
                        key={classification}
                        dataKey={classification} 
                        fill={classificationColors[index % classificationColors.length]}
                        name={classification}
                        label={<CustomLabel />}
                        maxBarSize={40}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Chart 4: Average Age by Created Month */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="Average Age of Closed TSI Tickets by Created Month (Last 12 Months)"
              sx={{ bgcolor: '#9C27B0', color: 'white' }}
            />
            <CardContent>
              {loadingAverageAgeByCreated ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                  <CircularProgress />
                </Box>
              ) : errorAverageAgeByCreated ? (
                <Alert severity="error">{errorAverageAgeByCreated}</Alert>
              ) : averageAgeByCreatedMonth.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                  <Typography color="textSecondary">No data available</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <ComposedChart data={averageAgeByCreatedMonth} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis yAxisId="left" label={{ value: 'Average Age (Days)', angle: -90, position: 'insideLeft' }} />
                    <YAxis yAxisId="right" orientation="right" label={{ value: 'Number of Tickets', angle: 90, position: 'insideRight' }} />
                    <Tooltip 
                      formatter={(value, name) => {
                        if (name === 'averageAge') return [`${value} days`, 'Average Age'];
                        if (name === 'ticketCount') return [value, 'Tickets Closed'];
                        return [value, name];
                      }}
                    />
                    <Legend />
                    <Bar 
                      yAxisId="left"
                      dataKey="averageAge" 
                      fill="#9C27B0" 
                      name="Average Age (Days)"
                    />
                    <Line 
                      yAxisId="right"
                      type="monotone" 
                      dataKey="ticketCount" 
                      stroke="#E91E63" 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ fill: '#E91E63', strokeWidth: 1, r: 4 }}
                      name="Number of Tickets"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Chart 5: Average Age by Closed Month */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="Average Age of Closed TSI Tickets by Closed Month (Last 12 Months)"
              sx={{ bgcolor: '#00BCD4', color: 'white' }}
            />
            <CardContent>
              {loadingAverageAgeByClosed ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                  <CircularProgress />
                </Box>
              ) : errorAverageAgeByClosed ? (
                <Alert severity="error">{errorAverageAgeByClosed}</Alert>
              ) : averageAgeByClosedMonth.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                  <Typography color="textSecondary">No data available</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <ComposedChart data={averageAgeByClosedMonth} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis yAxisId="left" label={{ value: 'Average Age (Days)', angle: -90, position: 'insideLeft' }} />
                    <YAxis yAxisId="right" orientation="right" label={{ value: 'Number of Tickets', angle: 90, position: 'insideRight' }} />
                    <Tooltip 
                      formatter={(value, name) => {
                        if (name === 'averageAge') return [`${value} days`, 'Average Age'];
                        if (name === 'ticketCount') return [value, 'Tickets Closed'];
                        return [value, name];
                      }}
                    />
                    <Legend />
                    <Bar 
                      yAxisId="left"
                      dataKey="averageAge" 
                      fill="#00BCD4" 
                      name="Average Age (Days)"
                    />
                    <Line 
                      yAxisId="right"
                      type="monotone" 
                      dataKey="ticketCount" 
                      stroke="#00ACC1" 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ fill: '#00ACC1', strokeWidth: 1, r: 4 }}
                      name="Number of Tickets"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TsiCharts;
